import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppBase, appPath } from '../components/appBase'
import { eventService } from '../services/eventService'
import type { EventForm, FormField } from '../data/types'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import {
  getSubmittedFormIds,
  submitFormResponse,
} from '../services/formState'

type Values = Record<string, string | number | boolean>

export default function FormDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const base = useAppBase()
  const [form, setForm] = useState<EventForm | undefined>()
  const [loading, setLoading] = useState(true)
  const [values, setValues] = useState<Values>({})
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    eventService.getForm(id).then((f) => {
      setForm(f)
      setLoading(false)
      setSubmitted(getSubmittedFormIds().has(id))
    })
  }, [id])

  const setValue = (fieldId: string, value: Values[string]) => {
    setValues((v) => ({ ...v, [fieldId]: value }))
    setErrors((e) => ({ ...e, [fieldId]: false }))
  }

  const requiredFields = useMemo(
    () => (form?.fields ?? []).filter((f) => f.required),
    [form],
  )

  const handleSubmit = async () => {
    if (!form) return
    const nextErrors: Record<string, boolean> = {}
    for (const f of requiredFields) {
      const v = values[f.id]
      if (v === undefined || v === '' || v === false) nextErrors[f.id] = true
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setSubmitting(true)
    await submitFormResponse(form.id, values)
    setSubmitting(false)
    setSubmitted(true)
  }

  if (loading) return <LoadingState />
  if (!form)
    return (
      <PageContainer>
        <EmptyState
          title="Form not found"
          action={
            <Button variant="contained" onClick={() => navigate(appPath(base, 'forms'))}>
              All forms
            </Button>
          }
        />
      </PageContainer>
    )

  return (
    <PageContainer maxWidth="sm">
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(appPath(base, 'forms'))}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        All forms
      </Button>

      <Typography variant="h4" fontWeight={700} gutterBottom>
        {form.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {form.description}
      </Typography>

      {submitted ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(46,125,50,0.06)',
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Response submitted
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Thank you — your response has been recorded.
          </Typography>
          <Button variant="contained" onClick={() => navigate(appPath(base, 'forms'))}>
            Back to forms
          </Button>
        </Box>
      ) : (
        <Stack spacing={3}>
          {form.fields.map((field) => (
            <FieldRenderer
              key={field.id}
              field={field}
              value={values[field.id]}
              error={errors[field.id]}
              onChange={(v) => setValue(field.id, v)}
            />
          ))}

          {Object.values(errors).some(Boolean) && (
            <Alert severity="error">Please complete the required fields.</Alert>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit response'}
          </Button>
        </Stack>
      )}
    </PageContainer>
  )
}

function FieldRenderer({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField
  value: Values[string] | undefined
  error?: boolean
  onChange: (value: Values[string]) => void
}) {
  const label = (
    <>
      {field.label}
      {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
    </>
  )

  switch (field.type) {
    case 'textarea':
      return (
        <TextField
          label={label}
          multiline
          minRows={3}
          fullWidth
          error={error}
          helperText={field.helperText}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'select':
      return (
        <TextField
          label={label}
          select
          fullWidth
          error={error}
          helperText={field.helperText}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options?.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      )
    case 'radio':
      return (
        <FormControl error={error}>
          <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>{label}</FormLabel>
          <RadioGroup
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          >
            {field.options?.map((opt) => (
              <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        </FormControl>
      )
    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={label}
        />
      )
    case 'rating':
      return (
        <FormControl error={error}>
          <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>{label}</FormLabel>
          <Rating
            value={typeof value === 'number' ? value : 0}
            onChange={(_, v) => onChange(v ?? 0)}
          />
        </FormControl>
      )
    default:
      return (
        <TextField
          label={label}
          fullWidth
          error={error}
          helperText={field.helperText}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
  }
}
